import { useCallback, useEffect, useState } from "react";
import { X, Eye, EyeOff, Loader2, ArrowLeft, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";
import {
  isFirebaseAuthConfigured,
  signInWithGoogle,
  signOutFromFirebase,
} from "../services/firebaseAuth";

// Google Icon
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const AuthModal = ({ isOpen, onClose, initialMode = "login" }) => {
  const { t } = useLanguage();
  const { loginWithEmail, loginWithSocial, register, requestPasswordReset } = useAuth();
  
  const [mode, setMode] = useState(initialMode === "phone" ? "login" : initialMode); // login, register, forgot
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [googleButtonError, setGoogleButtonError] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setResetSent(false);
    setShowPassword(false);
  };

  const changeMode = (newMode) => {
    resetForm();
    setGoogleButtonError("");
    setMode(newMode);
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
      setMode(initialMode === "phone" ? "login" : initialMode);
      setGoogleButtonError("");
    }
  }, [initialMode, isOpen]);

  const getAuthErrorMessage = useCallback((provider, error, code) => {
    const providerName = provider === "google" ? "Google" : "Social";

    switch (code) {
      case "firebase_not_configured":
      case "google_not_configured":
        return t("auth.googleUnavailable", "Google login is not configured yet.");
      case "auth/unauthorized-domain":
        return t(
          "auth.googleUnauthorizedDomain",
          "This domain is not authorized for Google login in Firebase.",
        );
      case "auth/popup-blocked":
        return t("auth.googlePopupBlocked", "Your browser blocked the Google sign-in popup.");
      case "auth/operation-not-allowed":
      case "auth/configuration-not-found":
      case "firebase_not_configured":
        return t("auth.firebaseUnavailable", "Firebase Auth is not configured yet.");
      case "auth/email-not-verified":
      case "firebase_email_not_verified":
        return t(
          "auth.emailNotVerified",
          "Please verify your email before signing in. We sent a new verification email. Check your inbox and spam/junk folder.",
        );
      case "auth/email-already-in-use":
        return t("auth.emailAlreadyInUse", "This email is already registered. Please sign in.");
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return t("auth.loginError", "Invalid email or password.");
      case "google_token_invalid":
        return t("auth.googleInvalidToken", "Google could not verify this sign-in. Please try again.");
      case "google_provider_unavailable":
        return t("auth.googleProviderUnavailable", "Google login is unavailable right now.");
      default:
        return error || t("auth.socialError", `${providerName} login failed. Please try again.`);
    }
  }, [t]);

  const getResetErrorMessage = useCallback((error, code) => {
    switch (code) {
      case "firebase_not_configured":
      case "email_not_configured":
        return t("auth.resetEmailUnavailable", "Password reset email is not configured yet.");
      case "auth/invalid-email":
      case "auth/user-not-found":
        return t("auth.resetLinkSent", "If the email exists, a reset link has been sent. Check your inbox and spam/junk folder.");
      case "email_delivery_failed":
        return t("auth.resetEmailDeliveryFailed", "Unable to send reset email right now.");
      default:
        return error || t("auth.resetError", "Failed to reset password");
    }
  }, [t]);

  const handleGoogleSignIn = useCallback(async () => {
    setLoading(true);
    setGoogleButtonError("");

    try {
      const firebaseResult = await signInWithGoogle();
      const result = await loginWithSocial("google", {
        idToken: firebaseResult.idToken,
        accessToken: firebaseResult.accessToken,
        tokenSource: "firebase",
      });

      if (result.success) {
        toast.success(t("auth.loginSuccess", "Welcome!"));
        onClose();
      } else {
        await signOutFromFirebase();
        toast.error(getAuthErrorMessage("google", result.error, result.code));
      }
    } catch (error) {
      if (error?.code === "auth/popup-closed-by-user" || error?.code === "auth/cancelled-popup-request") {
        return;
      }
      toast.error(getAuthErrorMessage("google", error.message, error.code));
    } finally {
      setLoading(false);
    }
  }, [getAuthErrorMessage, loginWithSocial, onClose, t]);

  useEffect(() => {
    if (isOpen && (mode === "login" || mode === "register") && !isFirebaseAuthConfigured) {
      setGoogleButtonError(t("auth.googleUnavailable", "Google login is not configured yet."));
    }
  }, [isOpen, mode, t]);

  if (!isOpen) return null;

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error(t("auth.fillAllFields", "Please fill all fields"));
      return;
    }
    setLoading(true);
    try {
      const result = await loginWithEmail(formData.email, formData.password);
      if (result.success) {
        toast.success(t("auth.loginSuccess", "Welcome back!"));
        onClose();
      } else {
        toast.error(getAuthErrorMessage("email", result.error, result.code));
      }
    } catch (error) {
      toast.error(t("auth.loginError", "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error(t("auth.fillAllFields", "Please fill all fields"));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(t("auth.passwordMismatch", "Passwords do not match"));
      return;
    }
    if (formData.password.length < 6) {
      toast.error(t("auth.passwordTooShort", "Password must be at least 6 characters"));
      return;
    }
    setLoading(true);
    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });
      if (result.success) {
        toast.success(
          t(
            "auth.verifyEmailSent",
            "Verification email sent. Please check your inbox and spam/junk folder, verify your email, then sign in.",
          ),
        );
        changeMode("login");
      } else {
        toast.error(getAuthErrorMessage("email", result.error, result.code));
      }
    } catch (error) {
      toast.error(t("auth.registerError", "Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Phone OTP auth intentionally disabled in the client. Odoo endpoints remain
  // unchanged for backward compatibility.

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error(t("auth.enterEmail", "Please enter your email"));
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordReset(formData.email);
      if (result.success) {
        setResetSent(true);
        toast.success(t("auth.resetLinkSent", "Password reset link sent. Check your inbox and spam/junk folder."));
      } else {
        toast.error(getResetErrorMessage(result.error, result.code));
      }
    } catch (error) {
      toast.error(t("auth.resetError", "Failed to send reset link"));
    } finally {
      setLoading(false);
    }
  };

  // Firebase reset emails contain their own hosted reset flow, so there is no
  // in-app reset-token form for web/mobile.

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10"
          data-testid="auth-close-btn"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="p-8 pb-0 text-center">
          <img src="/velaris-logo.png" alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")} className="h-10 mx-auto mb-6" />
          <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
            {mode === "login" && t("auth.signIn", "Sign In")}
            {mode === "register" && t("auth.createAccount", "Create Account")}
            {mode === "forgot" && t("auth.forgotPassword", "Forgot Password")}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
            {mode === "login" && t("auth.welcomeBack", "Welcome back! Please sign in to continue.")}
            {mode === "register" && t("auth.joinUs", "Join Velaris Royal for exclusive benefits.")}
            {mode === "forgot" && !resetSent && t("auth.resetDesc", "Enter your email to reset your password.")}
            {mode === "forgot" && resetSent && t(
              "auth.resetEmailInstructionsShort",
              "Check your email for password reset instructions.",
            )}
          </p>
        </div>

        <div className="p-8">
          {/* Social Login Buttons */}
          {(mode === "login" || mode === "register") && (
            <>
              <div className="space-y-3 mb-6">
                <div className="rounded-lg border border-gray-200 p-2 dark:border-gray-700" data-testid="google-login-btn">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="h-11 w-full rounded-full bg-white text-gray-900 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                    onClick={handleGoogleSignIn}
                    disabled={loading || !isFirebaseAuthConfigured}
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleIcon />
                    )}
                    <span>{t("auth.continueGoogle", "Continue with Google")}</span>
                  </Button>
                  {googleButtonError && (
                    <p className="mt-2 text-center text-xs text-amber-600 dark:text-amber-300">
                      {googleButtonError}
                    </p>
                  )}
                </div>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-900 text-gray-500">
                    {t("auth.or", "or")}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Email Login Form */}
          {mode === "login" && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.email", "Email")}
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t("auth.exampleEmail", "you@example.com")}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.password", "Password")}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t("auth.rememberMe", "Remember me")}</span>
                </label>
                <button
                  type="button"
                  onClick={() => changeMode("forgot")}
                  className="text-sm text-[#D4AF37] hover:text-[#C5A028]"
                  data-testid="forgot-password-link"
                >
                  {t("auth.forgotPassword", "Forgot password?")}
                </button>
              </div>
              <Button type="submit" className="w-full btn-gold text-black" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.signIn", "Sign In")}
              </Button>
            </form>
          )}

          {/* Register Form */}
          {mode === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.name", "Full Name")}
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t("auth.exampleName", "John Doe")}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.email", "Email")}
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t("auth.exampleEmail", "you@example.com")}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.password", "Password")}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.confirmPassword", "Confirm Password")}
                </label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="register-confirm-input"
                />
              </div>
              <Button type="submit" className="w-full btn-gold text-black" disabled={loading} data-testid="register-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.createAccount", "Create Account")}
              </Button>
            </form>
          )}

          {/* Phone OTP auth disabled: email/password and Google only. */}

          {/* Forgot Password Form */}
          {mode === "forgot" && !resetSent && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t("form.email", "Email")}
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t("auth.exampleEmail", "you@example.com")}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" className="w-full btn-gold text-black" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.sendResetLink", "Send Reset Link")}
              </Button>
            </form>
          )}

          {/* Reset Password Message */}
          {mode === "forgot" && resetSent && (
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center gap-2 text-green-500">
                <Check className="w-5 h-5" />
                <span className="text-sm">
                  {t("auth.resetLinkSent", "Password reset link sent. Check your inbox and spam/junk folder.")}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(
                  "auth.resetEmailInstructions",
                  "Open the email from Firebase to choose a new password, then come back and sign in.",
                )}
              </p>
            </div>
          )}

          {/* Mode Switchers */}
          <div className="mt-6 pt-6 border-t dark:border-gray-700">
            {mode === "login" && (
              <div className="space-y-3">
                {/* Phone login disabled: email/password and Google only. */}
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  {t("auth.noAccount", "Don't have an account?")} {" "}
                  <button onClick={() => changeMode("register")} className="text-[#D4AF37] hover:text-[#C5A028] font-medium" data-testid="switch-to-register-btn">
                    {t("auth.signUp", "Sign up")}
                  </button>
                </p>
              </div>
            )}
            {mode === "register" && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                {t("auth.haveAccount", "Already have an account?")}{" "}
                <button onClick={() => changeMode("login")} className="text-[#D4AF37] hover:text-[#C5A028] font-medium" data-testid="switch-to-login-btn">
                  {t("auth.signIn", "Sign in")}
                </button>
              </p>
            )}
            {mode === "forgot" && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                <button onClick={() => changeMode("login")} className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center justify-center gap-1 mx-auto">
                  <ArrowLeft className="w-4 h-4" />
                  {t("auth.backToLogin", "Back to login")}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
